import React, { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { getSnackbarOptions } from "../../../../../../../lib/getSnackbarOptions";
import {
  Dialog,
  LinearProgress,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
} from "@mui/material";
import { Skeleton } from "@mui/material";
import { Category } from "../../../../../../../models/category/category";
import { useApi } from "../../../../../../common/providers/Api.provider";
import { useTranslation } from "react-i18next";

export interface CategoryDeleteDialogComponentProps {
  open: boolean;
  categoryId?: number;
  handleClose: (result?: number) => void;
}

export function CategoryDeleteDialogComponent({
  open,
  categoryId,
  handleClose,
}: CategoryDeleteDialogComponentProps) {
  const { t: questionnaireTranslations } = useTranslation(
    "questionnaire-page",
    {
      keyPrefix: "categories.dialogs.delete",
      useSuspense: false,
    }
  );
  const { t: commonTranslations } = useTranslation("common", {
    useSuspense: false,
  });

  const api = useApi();
  const snackbar = useSnackbar();

  const [isLoadingState, setIsLoadingState] = useState<boolean>(false);
  const [nameState, setNameState] = useState<string>("");
  const [categoryState, setCategoryState] = useState<Category | undefined>(
    undefined
  );

  const getCategory = async (categoryId: number) => {
    try {
      const response = await api.categoryById(categoryId);

      setCategoryState(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (category?: Category) => {
    if (!category || !category.id) {
      return;
    }

    try {
      setIsLoadingState(true);

      const response = await api.categoriesDelete(category.id);

      if (response.success) {
        snackbar.enqueueSnackbar(
          response.message,
          getSnackbarOptions({ variant: "success" })
        );
      } else {
        snackbar.enqueueSnackbar(
          response.message,
          getSnackbarOptions({ variant: "error" })
        );
      }

      handleClose(category.id);
    } catch (e) {
      snackbar.enqueueSnackbar(
        e as String,
        getSnackbarOptions({
          variant: "error",
        })
      );
    } finally {
      setNameState("");
      setIsLoadingState(false);
    }
  };

  const renderContentText = () => {
    if (!categoryState) {
      return;
    }

    if (!categoryState.steps.length) {
      return questionnaireTranslations("repeatNameDescription", {
        category: categoryState.name,
      });
    }

    return (
      <>
        <p>{questionnaireTranslations("linkedTo")}</p>
        <ul>
          {categoryState.steps.map((step, index) => (
            <li key={index}>{step.name}</li>
          ))}
        </ul>
        <p>{questionnaireTranslations("reason")}</p>
      </>
    );
  };

  const renderNameFormField = () => {
    if (!categoryState) {
      return <Skeleton variant="text" />;
    }

    if (!categoryState.steps.length) {
      return (
        <TextField
          autoFocus
          fullWidth
          required
          autoComplete="off"
          variant="outlined"
          margin="dense"
          id="name"
          label={questionnaireTranslations("repeatName")}
          type="text"
          value={nameState}
          onChange={(event) => setNameState(event.target.value)}
        />
      );
    }
  };

  const renderDeleteButton = () => {
    if (!categoryState) {
      return <Skeleton variant="rectangular" />;
    }

    if (!categoryState.steps.length) {
      return (
        <Button
          disabled={categoryState.name !== nameState}
          onClick={() => handleDelete(categoryState)}
          color="primary"
        >
          {commonTranslations("remove")}
        </Button>
      );
    }
  };

  useEffect(() => {
    if (!!categoryId) {
      getCategory(categoryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  return (
    <Dialog open={open}>
      {isLoadingState && <LinearProgress />}
      <DialogTitle>
        {!categoryState ? (
          <Skeleton variant="text" />
        ) : (
          questionnaireTranslations("title", { category: categoryState.name })
        )}
      </DialogTitle>
      <DialogContent>
        {!categoryState ? (
          <Skeleton variant="rectangular" />
        ) : (
          renderContentText()
        )}
        {renderNameFormField()}
      </DialogContent>
      <DialogActions>
        {!categoryState ? (
          <Skeleton variant="rectangular" />
        ) : (
          <Button
            onClick={() => {
              setNameState("");
              handleClose(undefined);
            }}
            color="secondary"
          >
            {commonTranslations("cancel")}
          </Button>
        )}
        {renderDeleteButton()}
      </DialogActions>
    </Dialog>
  );
}
